// ============================================================
// Motor de cálculo de estrés térmico — Índice WBGT
// (Wet Bulb Globe Temperature)
//
// Fuente: NTP 1189 (INSST) — Evaluación del estrés térmico. Índice WBGT.
// Tabla de referencia de límites WBGT por tasa metabólica: ISO 7243:2017,
// Table 1, tal como se cita en Parsons K., "Heat Stress Standard ISO 7243
// and its Global Application".
//
// Motor de cálculo puro, sin UI:
//   - WBGT interior (sin radiación solar): 0.7·thn + 0.3·tg
//   - WBGT exterior (con radiación solar): 0.7·thn + 0.2·tg + 0.1·ta
//   - Tasa metabólica promedio ponderada por % de tiempo trabajado vs. reposo
//   - Clasificación de esa M promedio en una de las 5 categorías ISO 7243
//   - Verificación de cumplimiento contra WBGT_REF según aclimatación
// ============================================================

export type TasaMetabolica = 'reposo' | 'ligera' | 'moderada' | 'pesada' | 'muy_pesada';

// Valor representativo (punto medio) de cada banda de tasa metabólica, en
// W/m² — tabla de referencia ISO 7243:2017, vía NTP 1189 INSST. Se usa como
// insumo del promedio ponderado en calcularTasaMetabolicaPromedio().
// Nota sobre muy_pesada: la banda fuente es abierta (M > 260, sin límite
// superior definido en la tabla), así que 290 no es un punto medio exacto
// como en las otras 4 bandas — es un valor aproximado asumido para tener un
// representativo numérico utilizable en el promedio ponderado.
export const TASA_METABOLICA_VALOR_REPRESENTATIVO: Record<TasaMetabolica, number> = {
  reposo: 50,
  ligera: 100,
  moderada: 165,
  pesada: 230,
  muy_pesada: 290,
};

// Límites WBGT (°C) por tasa metabólica y estado de aclimatación al calor —
// ISO 7243:2017, Table 1, citada en Parsons K., "Heat Stress Standard
// ISO 7243 and its Global Application". No modificar sin repetir esa
// verificación.
export const WBGT_REF: Record<TasaMetabolica, { aclimatado: number; noAclimatado: number }> = {
  reposo:     { aclimatado: 33, noAclimatado: 32 },
  ligera:     { aclimatado: 30, noAclimatado: 29 },
  moderada:   { aclimatado: 28, noAclimatado: 26 },
  pesada:     { aclimatado: 25, noAclimatado: 22 },
  muy_pesada: { aclimatado: 23, noAclimatado: 18 },
};

// ===== 1. Índice WBGT =====

// NTP 1189: WBGT interior (sin radiación solar directa) a partir de la
// temperatura de globo (tg) y la temperatura húmeda natural (thn).
export function calcularWBGTInterior(tg: number, thn: number): number {
  return 0.7 * thn + 0.3 * tg;
}

// NTP 1189: WBGT exterior (con radiación solar directa) — incorpora además
// la temperatura del aire (ta, bulbo seco).
export function calcularWBGTExterior(tg: number, thn: number, ta: number): number {
  return 0.7 * thn + 0.2 * tg + 0.1 * ta;
}

// ===== 2. Tasa metabólica promedio ponderada =====

// NTP 1189: cuando el trabajador alterna entre una tarea con carga
// metabólica y períodos de reposo dentro del ciclo evaluado, la M a
// comparar contra WBGT_REF es el promedio ponderado entre el valor
// representativo de la tarea (tasaTrabajo) y el de reposo, según el
// porcentaje de tiempo (0-100) efectivamente trabajado en ese ciclo.
export function calcularTasaMetabolicaPromedio(
  tasaTrabajo: TasaMetabolica,
  porcentajeTiempoTrabajado: number
): number {
  const valorTrabajo = TASA_METABOLICA_VALOR_REPRESENTATIVO[tasaTrabajo];
  const valorReposo = TASA_METABOLICA_VALOR_REPRESENTATIVO.reposo;
  const fraccionTrabajada = porcentajeTiempoTrabajado / 100;
  return fraccionTrabajada * valorTrabajo + (1 - fraccionTrabajada) * valorReposo;
}

// ===== 3. Clasificación de la M promedio en categoría ISO 7243 =====

// Rangos de M (W/m²) según la tabla ISO 7243:2017: reposo <65, ligera
// 65-130, moderada 130-200, pesada 200-260, muy_pesada >260. Los límites
// exactos de banda (M=65, 130, 200, 260) no están resueltos explícitamente
// en la tabla fuente (que usa desigualdades estrictas en ambos lados). Se
// asignan aquí a la banda superior porque, en esta tabla, a mayor tasa
// metabólica el límite WBGT permitido es más estricto (más bajo) —
// asignar el caso límite a la banda superior es la interpretación
// conservadora en seguridad: ante la duda, exige un ambiente más fresco
// para considerar "cumple", nunca más laxo. Criterio confirmado por Kevin
// el 28/09/2026.
export function determinarCategoriaPorM(mPromedio: number): TasaMetabolica {
  if (mPromedio < 65) return 'reposo';
  if (mPromedio < 130) return 'ligera';
  if (mPromedio < 200) return 'moderada';
  if (mPromedio < 260) return 'pesada';
  return 'muy_pesada';
}

// ===== 4. Verificación de cumplimiento =====

// NTP 1189: el WBGT medido/calculado cumple si no supera el límite de
// WBGT_REF correspondiente a la categoría de M y al estado de aclimatación
// del trabajador.
export function verificarCumplimientoWBGT(
  wbgt: number,
  categoria: TasaMetabolica,
  aclimatado: boolean
): { cumple: boolean; limite: number } {
  const limite = aclimatado ? WBGT_REF[categoria].aclimatado : WBGT_REF[categoria].noAclimatado;
  return { cumple: wbgt <= limite, limite };
}
